/**
 * Case-study content for the showcased experience entries.
 *
 * Project 01 is transcribed verbatim from the design system's own
 * `ui_kits/portfolio/Project.jsx` template.
 *
 * NEEDS REVIEW — only project 01 has supplied narrative copy. For 02, 03 and 04
 * the brief gives just the headline figures (30+ JSA documents, 100+ students
 * taught, 159K+ content views) plus role, organisation and period. The leads
 * and bullets below are drafted conservatively around those facts and invent no
 * new numbers, but Mafazi should confirm the wording before this goes public.
 *
 * Note also that 02 is a slice of the same Pertamina internship as 01 — the
 * document-review workstream. It is written to focus on method so the two pages
 * complement rather than repeat each other; worth checking they still read as
 * distinct pieces of work.
 */

/**
 * One section of the long-form case study.
 *
 * `paragraphs` holds only what the brief actually supports, so everything here
 * is publishable as written. A section with no paragraphs is one still awaiting
 * Mafazi's answers: it renders as a draft block in development and is omitted
 * from production entirely.
 *
 * The outstanding questions live in `drafts.ts`, keyed by slug and heading —
 * deliberately not a field here, so they are not bundled into production JS.
 */
export type ArticleSection = {
  heading: string;
  paragraphs?: string[];
};

export type ProjectStat = { value: string; label: string };
export type ProjectSpec = { label: string; value: string };
export type ProjectFigure = { src: string; alt: string; index: string; caption: string; ratio?: string };

export type Project = {
  slug: string;
  /** Index as shown on the home page card. */
  index: string;
  /** Card title on the home page. */
  cardTitle: string;
  /** Longer headline on the case-study page. */
  title: string;
  src: string;
  alt: string;
  meta: string;
  tags: string[];
  lead: string;
  bullets: string[];
  /** Long-form body. See {@link ArticleSection} and drafts.ts. */
  sections: ArticleSection[];
  stats: ProjectStat[];
  spec: ProjectSpec[];
  hero: ProjectFigure;
  figures: ProjectFigure[];
  next: { label: string; href: string };
  seo: { title: string; description: string };
};

/**
 * TODO: Unsplash placeholders throughout — swap for real photographs.
 *
 * Each project's card image doubles as its detail-page hero, so clicking
 * through carries the same photograph. All four card images are distinct.
 *
 * The reference markup pointed card 01 at photo-1518709594023, which is a stock
 * photo of zebras. Dropped rather than reproduced.
 */
const IMG = {
  /** Refinery at dusk. */
  rig: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?w=1800&auto=format&fit=crop",
  /** Technical drawings and drafting tools. */
  lab: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1400&auto=format&fit=crop",
  /** Site workers in high-visibility gear. */
  site: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&auto=format&fit=crop",
  /** Operator at laboratory measurement equipment. */
  desk: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1400&auto=format&fit=crop",
  /** Hard-hat inspection. */
  docs: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1400&auto=format&fit=crop",
  /** Laptop, notebook and coffee — editorial desk. */
  editorial: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1400&auto=format&fit=crop",
};

export const projects: Project[] = [
  {
    slug: "safety-climate-study",
    index: "01",
    cardTitle: "Safety climate study, drilling operations",
    title: "Safety climate research across drilling operations.",
    // Not IMG.rig: that is the home page's hero, and reusing it here made the
    // case study look like the landing page reloading.
    src: IMG.site,
    alt: "Site personnel in high-visibility gear",
    meta: "PT Pertamina Drilling Services Indonesia · HSSE&Q Intern · Jun 2026 – present",
    tags: ["HSSE", "Research"],
    lead: "An eight-week HSSE&Q project studying how safety is actually practised on drilling operations — what people do, what the documents say, and where the two diverge.",
    bullets: [
      "Conducted safety climate research involving 1 field observation, 2 Focus Group Discussions, and 50+ employee interviews across drilling operations.",
      "Reviewed and analyzed 30+ Job Safety Analysis documents to identify operational risks and human factor issues.",
      "Evaluated safety practices across 2 operation rig activities and synthesized findings into actionable improvement recommendations.",
      "Collaborated with cross-functional HSSE and operational personnel throughout the project.",
    ],
    sections: [
      {
        heading: "Context",
        paragraphs: [
          "An eight-week HSSE&Q project at PT Pertamina Drilling Services Indonesia, studying how safety is actually practised on drilling operations — what people do, what the documents say, and where the two diverge.",
        ],
      },
      {
        heading: "Approach",
        paragraphs: [
          "The work combined one field observation, two Focus Group Discussions, and 50+ employee interviews, set against a review of 30+ Job Safety Analysis documents covering two operation rig activities.",
        ],
      },
      {
        heading: "What I found",
        paragraphs: [
          "Operational risks and human factor issues were identified across the documentation, and safety practices were evaluated across two operation rig activities.",
        ],
      },
      {
        heading: "What changed",
      },
    ],
    stats: [
      { value: "50+", label: "interviews" },
      { value: "30+", label: "JSA documents" },
      { value: "2", label: "focus group discussions" },
      { value: "2", label: "rig activities evaluated" },
    ],
    spec: [
      { label: "Organisation", value: "PT Pertamina Drilling Services Indonesia" },
      { label: "Role", value: "HSSE&Q Intern" },
      { label: "Location", value: "Jakarta Raya, Indonesia" },
      { label: "Period", value: "Jun 2026 – present" },
      { label: "Duration", value: "8-week internship project" },
      { label: "Methods", value: "Field observation, FGD, interviews, document review" },
    ],
    hero: {
      src: IMG.site,
      alt: "Site personnel in high-visibility gear",
      ratio: "21/9",
      index: "FIG. 01",
      caption: "Rig operations under observation during the internship project.",
    },
    figures: [
      {
        src: IMG.rig,
        alt: "Drilling and processing facility at dusk",
        ratio: "4/3",
        index: "FIG. 02",
        caption: "Document review: Job Safety Analysis against observed practice.",
      },
      {
        src: IMG.docs,
        alt: "Equipment inspection in progress",
        ratio: "4/3",
        index: "FIG. 03",
        caption: "Findings synthesised with cross-functional HSSE and operational personnel.",
      },
    ],
    next: { label: "Job Safety Analysis review", href: "/work/job-safety-analysis-review/" },
    seo: {
      title: "Safety climate study, drilling operations — Mafazi Ikhwan Dhandi Hibatullah",
      description:
        "An eight-week HSSE&Q project at PT Pertamina Drilling Services Indonesia: 50+ employee interviews, 30+ Job Safety Analysis documents, and 2 Focus Group Discussions across drilling operations.",
    },
  },
  {
    slug: "job-safety-analysis-review",
    index: "02",
    cardTitle: "Job Safety Analysis review",
    title: "Reading Job Safety Analysis against the work as it is done.",
    src: IMG.docs,
    alt: "Equipment inspection in progress",
    meta: "30+ JSA documents · Operational risk and human factors · Jakarta",
    tags: ["HSSE", "Data"],
    lead: "The document-review workstream of the Pertamina internship: reading Job Safety Analysis paperwork across drilling operations and recording where operational risk and human factors are not carried through into the controls.",
    bullets: [
      "Reviewed and analyzed 30+ Job Safety Analysis documents across drilling operations.",
      "Identified operational risks and human factor issues recorded in the documentation.",
      "Compared documented controls against practice observed on site.",
      "Summarised findings for the HSSE team as input to improvement recommendations.",
    ],
    sections: [
      {
        heading: "Context",
        paragraphs: [
          "The document-review workstream of the Pertamina internship. A Job Safety Analysis sets out the hazards of a task and the controls meant to contain them; the question here was whether those controls survive contact with the work as it is actually done.",
        ],
      },
      {
        heading: "Approach",
        paragraphs: [
          "30+ Job Safety Analysis documents were read across drilling operations, and their stated controls checked against practice observed on site.",
        ],
      },
      {
        heading: "What I found",
        paragraphs: [
          "Operational risks and human factor issues were identified in the documentation and recorded against the activities they related to.",
        ],
      },
      {
        heading: "What changed",
      },
    ],
    stats: [{ value: "30+", label: "JSA documents reviewed" }],
    spec: [
      { label: "Organisation", value: "PT Pertamina Drilling Services Indonesia" },
      { label: "Role", value: "HSSE&Q Intern" },
      { label: "Location", value: "Jakarta Raya, Indonesia" },
      { label: "Period", value: "Jun 2026 – present" },
      { label: "Scope", value: "30+ Job Safety Analysis documents" },
      { label: "Methods", value: "Document review, operational risk analysis, human factors assessment" },
    ],
    hero: {
      src: IMG.docs,
      alt: "Equipment inspection in progress",
      ratio: "21/9",
      index: "FIG. 01",
      caption: "Job Safety Analysis documentation checked against the task as performed.",
    },
    figures: [
      {
        src: IMG.site,
        alt: "Site personnel in high-visibility gear",
        ratio: "4/3",
        index: "FIG. 02",
        caption: "Documented controls checked against operations as observed.",
      },
      {
        src: IMG.lab,
        alt: "Technical drawings under analysis",
        ratio: "4/3",
        index: "FIG. 03",
        caption: "Findings recorded per activity and returned to the HSSE team.",
      },
    ],
    next: { label: "Ergonomics & work systems laboratory", href: "/work/ergonomics-laboratory/" },
    seo: {
      title: "Job Safety Analysis review — Mafazi Ikhwan Dhandi Hibatullah",
      description:
        "Review and analysis of 30+ Job Safety Analysis documents across drilling operations at PT Pertamina Drilling Services Indonesia, identifying operational risks and human factor issues.",
    },
  },
  {
    slug: "ergonomics-laboratory",
    index: "03",
    cardTitle: "Ergonomics & work systems laboratory",
    title: "Ergonomics and work systems, taught in the laboratory.",
    src: IMG.desk,
    alt: "Operator at laboratory measurement equipment",
    meta: "LRSKE ITB · Laboratory Assistant · Jan 2026 – present",
    tags: ["Ergonomics", "Teaching"],
    lead: "Laboratory instruction at LRSKE ITB — running practical sessions in work systems engineering and ergonomics, and keeping measurement practice consistent across cohorts.",
    bullets: [
      "Taught 100+ students across work systems engineering and ergonomics laboratory sessions.",
      "Guided practical modules and supervised student teams through the laboratory curriculum.",
      "Assessed laboratory reports and presentations, with written feedback on method and analysis.",
      "Maintained measurement protocols so results stay comparable between cohorts.",
    ],
    sections: [
      {
        heading: "Context",
        paragraphs: [
          "The Work Systems Engineering & Ergonomics Laboratory (LRSKE) at ITB runs the practical half of the work systems and ergonomics curriculum. Laboratory assistants teach the sessions, assess the work, and keep measurement practice consistent from one cohort to the next.",
        ],
      },
      {
        heading: "How the sessions run",
      },
      {
        heading: "Teaching and assessment",
        paragraphs: [
          "Across these sessions 100+ students have been taught, supervised in teams, and assessed on laboratory reports and presentations with written feedback on method and analysis.",
        ],
      },
      {
        heading: "Keeping results comparable",
        paragraphs: [
          "Measurement protocols are maintained so that results stay comparable between cohorts.",
        ],
      },
    ],
    stats: [{ value: "100+", label: "students taught" }],
    spec: [
      { label: "Organisation", value: "Work Systems Engineering & Ergonomics Laboratory (LRSKE) ITB" },
      { label: "Role", value: "Laboratory Assistant" },
      { label: "Location", value: "Bandung, Indonesia" },
      { label: "Period", value: "Jan 2026 – present" },
      { label: "Focus", value: "Work systems engineering, ergonomics, measurement practice" },
    ],
    hero: {
      src: IMG.desk,
      alt: "Operator at laboratory measurement equipment",
      ratio: "21/9",
      index: "FIG. 01",
      caption: "Work systems and ergonomics practicals at LRSKE ITB.",
    },
    figures: [
      {
        src: IMG.lab,
        alt: "Technical drawings and drafting tools",
        ratio: "4/3",
        index: "FIG. 02",
        caption: "Measurement protocols kept consistent across cohorts.",
      },
      {
        src: IMG.site,
        alt: "Team working through a practical task",
        ratio: "4/3",
        index: "FIG. 03",
        caption: "Student teams supervised through the laboratory curriculum.",
      },
    ],
    next: { label: "MTI Insight content strategy", href: "/work/mti-insight-content-strategy/" },
    seo: {
      title: "Ergonomics & work systems laboratory — Mafazi Ikhwan Dhandi Hibatullah",
      description:
        "Laboratory Assistant at the Work Systems Engineering & Ergonomics Laboratory (LRSKE) ITB, teaching 100+ students across work systems engineering and ergonomics practical sessions.",
    },
  },
  {
    slug: "mti-insight-content-strategy",
    index: "04",
    cardTitle: "MTI Insight content strategy",
    title: "Content that reaches people, planned and published.",
    src: IMG.editorial,
    alt: "Laptop and notebook on an editorial desk",
    meta: "Keluarga Mahasiswa Teknik Industri ITB · Vice Manager · May 2026 – present",
    tags: ["Leadership", "Communication"],
    lead: "Content for MTI Insight, the publication of Keluarga Mahasiswa Teknik Industri ITB — deciding what gets published, and running the editorial process behind it.",
    bullets: [
      "Reached 159K+ content views across MTI Insight publications.",
      "Planned and scheduled the content calendar as Vice Manager.",
      "Coordinated writers and designers through drafting, review, and publication.",
      "Set review steps so published pieces hold a consistent standard.",
    ],
    sections: [
      {
        heading: "Context",
        paragraphs: [
          "MTI Insight is the publication of Keluarga Mahasiswa Teknik Industri ITB. As Vice Manager the job is deciding what gets published and running the process that gets it there.",
        ],
      },
      {
        heading: "Approach",
      },
      {
        heading: "Reach",
        paragraphs: ["Content published through MTI Insight has reached 159K+ views."],
      },
      {
        heading: "What changed",
        paragraphs: [
          "Review steps were set so that published pieces hold a consistent standard.",
        ],
      },
    ],
    stats: [{ value: "159K+", label: "content views" }],
    spec: [
      { label: "Organisation", value: "Keluarga Mahasiswa Teknik Industri ITB" },
      { label: "Role", value: "Vice Manager" },
      { label: "Location", value: "Bandung, Indonesia" },
      { label: "Period", value: "May 2026 – present" },
      { label: "Focus", value: "Content strategy, editorial process, publication" },
    ],
    hero: {
      src: IMG.editorial,
      alt: "Laptop and notebook on an editorial desk",
      ratio: "21/9",
      index: "FIG. 01",
      caption: "Editorial planning for MTI Insight.",
    },
    figures: [
      {
        src: IMG.lab,
        alt: "Drafts and notes laid out for review",
        ratio: "4/3",
        index: "FIG. 02",
        caption: "The content calendar, planned and scheduled ahead of publication.",
      },
      {
        src: IMG.desk,
        alt: "Contributor at work",
        ratio: "4/3",
        index: "FIG. 03",
        caption: "Writers and designers coordinated through drafting and review.",
      },
    ],
    next: { label: "Safety climate study, drilling operations", href: "/work/safety-climate-study/" },
    seo: {
      title: "MTI Insight content strategy — Mafazi Ikhwan Dhandi Hibatullah",
      description:
        "Vice Manager for MTI Insight at Keluarga Mahasiswa Teknik Industri ITB, reaching 159K+ content views through planned editorial process and publication.",
    },
  },
];

export function projectHref(project: Project): string {
  return `/work/${project.slug}/`;
}

export function projectBySlug(slug: string): Project {
  const found = projects.find((p) => p.slug === slug);
  if (!found) throw new Error(`Unknown project slug: ${slug}`);
  return found;
}
